/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { ValidationRule, WrappedFormUtils } from "antd/lib/form/Form"
import { ReactNode, ChangeEvent } from "react"
import { isPlainObject, isEmpty, forOwn, isUndefined } from "lodash"
import { Weight } from "types/models/menu"
import { AdvancedPricingConfig } from "store/duck/global.type"

export const stringValidations = {
  alphanumerical: /^[-a-zA-Z0-9-()]+[a-zA-Z0-9 -.]*$/,
  alphanumericalAndJustComma: /^[-a-zA-Z0-9]+[a-zA-Z0-9,_-]*$/,
  alphabetical: new RegExp("^[A-Za-z -.]+$"),
  numerical: new RegExp("^[0-9-., ]+$"),
  integerOnly: new RegExp("^[0-9]+$"),
  numericalOnly: /^[1-9][0-9]\d*(\.\d{1,2})?$/,
  numericalWithDash: new RegExp("^[0-9-]+$"),
  numericalAmount: /^([1-9]\d*|(?=.*[1-9])\d*(?:\.\d{1,2}))?$/,
  numericalAmountNoDecimal: /^([1-9]\d*|(?=.*[1-9])\d*)?$/,
  numericalAmountNoDecimalAllowZero: /^([1-9]\d*|(?=.*[1-9])\d*|0)?$/,
  numericalAmountAllowZero: /^([1-9]\d*|0\.\d{1,2}|[1-9]\d*(\.\d{1,2})|0)?$/,
  numericalAmountAllowZeroNoDecimal: /^([1-9]\d*|0|[1-9]\d*|0)?$/,
  numericalOnlyWithNegative: /^(-?[1-9]\d*|-?(?=.*[1-9])\d*(?:\.\d{1,2})|-?[1-9]\d*(\.\d{1,2})|0)?$/,
  numericalOnlyWithNegativeNoDecimal: /^(-?[1-9]\d*|-?(?=.*[1-9])\d*|-?[1-9]\d*|0)?$/,
  mobileNumber: /^((\+\d{1,3}[- ]?)|0)\d+$/,
  moreThanZero: /^[1-9]\d*(\.\d+)?$/,
  moreThanZeroDecimal: /^(0*[1-9][0-9]*(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/,
  required: /.+/,
}

export const isValidStringValue = (strValue: any) => {
  const isNumber =
    strValue !== "" &&
    stringValidations.numericalOnlyWithNegative.test(strValue)
  const split = strValue.split(".")
  if (isNumber && split.length === 2 && Number(split[1]) === 0) {
    return false
  }
  return isNumber
}

export const eventValueToNumber = (e: any) => {
  const noCommas = e.target.value.replace(/,/g, ".")
  return isValidStringValue(noCommas) ? Number(noCommas) : noCommas
}

export const eventValueToCleanNumericInput = (
  e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  allowedDecimals = 0,
): string => {
  const myString = e.target.value.replace(/[^.\d]/g, "")
  const generalNumInputRegex = /^\d+/
  const decimalInputRegex = new RegExp(
    `\\.[0-9]${allowedDecimals === Infinity ? "*" : `{0,${allowedDecimals}}`}`,
  )
  const withDecimalInputRegex = new RegExp(
    `${generalNumInputRegex.source}(${decimalInputRegex.source})?`,
  )
  const testRegex =
    allowedDecimals > 0 ? withDecimalInputRegex : generalNumInputRegex
  let parsedNumericInput = testRegex.exec(myString)?.[0]
  if (!parsedNumericInput) {
    return ""
  }
  // remove left-trailing 0 from input e.g. 01.142 -> 1.142; 0.142 -> 0.142
  parsedNumericInput = parsedNumericInput.replace(
    new RegExp(`^[0]+(?=([0]{1}|[1-9]+)(${decimalInputRegex.source})?)`),
    "",
  )
  return parsedNumericInput
}

export interface DeferredValidatorRule {
  pattern: RegExp
  message: string
  ignoreEmptyValue?: boolean
}

export interface DeferredValidator {
  /**
   * An antd rule validator that clears errors once validation for {@code onSaveRules}
   * succeeds, but does not return validation errors back to the form (because the user
   * might be in the middle of typing the correct value).
   *
   * Also does field validations specified on {@code onChangeRules} if and only if
   * the field has been touched (whereas antd does the validation without this check).
   *
   * Put this as a custom validator in antd's {@code getFieldDecorator} function.
   */
  onChangeRuleErrorClearerValidator: NonNullable<ValidationRule["validator"]>

  /**
   * Validate price on save, using the rules in {@code onSaveRules}.
   * Errors are cleared by {@code onChangeRuleErrorClearerValidator}.
   *
   * Use this in your form submission handler to do validations
   * before you actually persist the form values onto BE.
   *
   * @returns true if valid, false otherwise
   */
  validateOnSave: (price: string, fieldKey: string) => boolean
}

/**
 * Creates a better rule validator for price and decimal-ish inputs in Antd forms (because rule-specific validationTrigger only exists in v4).
 * Not a class because antd binds custom validators, which renders this.* useless.
 * @param onSaveRules rules that are tested for when you call {@code validateOnSave}
 * @param onChangeRules rules that are tested for when {@code onChangeRuleErrorClearerValidator} is run
 */
export function newDeferredValidator(
  form: WrappedFormUtils<any>,
  onSaveRules: Array<DeferredValidatorRule> = [],
  onChangeRules: Array<DeferredValidatorRule> = [],
): DeferredValidator {
  return {
    onChangeRuleErrorClearerValidator: (rule, value, callback) => {
      const { fullField } = rule
      // validate on change
      if (form.isFieldTouched(fullField)) {
        const failedOnChangeRule = onChangeRules.find(
          r => !r.pattern.test(value),
        )
        if (failedOnChangeRule) {
          callback(failedOnChangeRule.message)
          return
        }
      }
      // clear save errors
      if (onSaveRules.every(r => r.pattern.test(value))) {
        callback()
      } else {
        callback(form.getFieldError(fullField)?.[0])
      }
    },

    validateOnSave: (price: string, fieldKey: string): boolean => {
      const isValid = !onSaveRules.some(
        ({ pattern, message, ignoreEmptyValue }) => {
          if (!(ignoreEmptyValue && !price) && !pattern.test(price)) {
            if (!form.getFieldError(fieldKey)?.length) {
              form.setFields({
                [fieldKey]: {
                  value: price,
                  errors: [
                    ...(form.getFieldError(fieldKey) ?? []),
                    new Error(message),
                  ],
                },
              })
            }
            return true
          }
          return false
        },
      )
      return isValid
    },
  }
}

export function newPriceDiffValidator(
  maxChangeRatio: number,
  errMsg: string,
  baseValue?: string | number | null,
): NonNullable<ValidationRule["validator"]> {
  return (rule, value, callback) => {
    const currencyExponent = 2
    // hardcode the exponent because it doesn't actually matter since we're just doing ratio-based validation
    // needed to solve floating point errors, e.g. SGD1.05 - SGD1 = 0.050000000044, which fails validation
    const valueNum = Number(value) * 10 ** currencyExponent
    const baseValueNum = Number(baseValue) * 10 ** currencyExponent
    if (
      value === null ||
      baseValue === null ||
      value === "" ||
      baseValue === "" ||
      Number.isNaN(valueNum) ||
      Number.isNaN(baseValueNum) ||
      baseValueNum === 0 // for modifier case where baseValue can be 0
    ) {
      callback()
      return
    }
    const priceDiff = Math.abs(baseValueNum - valueNum)
    const priceDiffRatio = priceDiff / baseValueNum
    if (priceDiffRatio > maxChangeRatio) {
      callback(errMsg)
      return
    }
    callback()
  }
}

export function makeBcrsUnitCountRangeValidator(
  min: number,
  max: number,
  message: string,
): NonNullable<ValidationRule["validator"]> {
  return (rule, value, callback) => {
    if (value === undefined || value === null || value === "") {
      callback() // defer to the separate `required` rule
      return
    }
    const numericValue = Number(value)
    if (
      Number.isNaN(numericValue) ||
      !Number.isInteger(numericValue) ||
      numericValue < min ||
      numericValue > max
    ) {
      callback(message)
      return
    }
    callback()
  }
}

/**
 * Validator for the FMQ per-group "Maximum quantity" cap (BP-48453).
 * Two distinct error states, matching the Figma Keynames ref:
 *  - value ≤ 1 (or non-integer) → `moreThanOneMessage`
 *    ("This should be more than 1 to allow multiple quantity")
 *  - value > max selections     → `smallerEqualMessage`
 *    ("This should be smaller or equal to the maximum number of selections")
 */
export function makeFmqMaxQuantityValidator(
  max: number,
  moreThanOneMessage: string,
  smallerEqualMessage: string,
): NonNullable<ValidationRule["validator"]> {
  return (rule, value, callback) => {
    if (value === undefined || value === null || value === "") {
      callback() // defer to the separate `required` rule
      return
    }
    const numericValue = Number(value)
    if (
      Number.isNaN(numericValue) ||
      !Number.isInteger(numericValue) ||
      numericValue <= 1
    ) {
      callback(moreThanOneMessage)
      return
    }
    if (numericValue > max) {
      callback(smallerEqualMessage)
      return
    }
    callback()
  }
}

type FieldErrors = {
  [key: string]: FieldErrors | string[] | undefined
}

export function hasFormFieldsErrors(
  fieldsError: FieldErrors | string[] | undefined,
  ignoreFields: Array<string> = [],
): boolean {
  if (!isPlainObject(fieldsError)) return !isEmpty(fieldsError)
  let hasError = false
  forOwn(fieldsError, (value: FieldErrors, key) => {
    if (ignoreFields.includes(key)) return
    hasError = hasError || hasFormFieldsErrors(value, ignoreFields)
  })

  return hasError
}

export const hasFormErrors = (
  form: WrappedFormUtils,
  ignoreFields: Array<string> = [],
) => {
  const fieldsError = form.getFieldsError()
  return hasFormFieldsErrors(fieldsError, ignoreFields)
}

export const formErrorsDetail = (
  fieldsError: any,
  parentKey: string = "",
  finalValue: string[] = [],
) => {
  if (fieldsError) {
    Object.keys(fieldsError).forEach((field: string) => {
      const errorKey = parentKey ? `${parentKey}.${field}` : field

      // validate nested object error message
      if (
        typeof fieldsError[field] === "object" &&
        !Array.isArray(fieldsError[field])
      ) {
        formErrorsDetail(fieldsError[field], errorKey, finalValue)
      } else if (!isUndefined(fieldsError[field])) {
        finalValue.push(errorKey)
      }
    })
  }
  return finalValue
}

interface ValidationProps {
  validateStatus:
    | ""
    | "error"
    | "success"
    | "warning"
    | "validating"
    | undefined
  help: ReactNode
}

// Only show error after a field is touched.
export function getValidationProps(
  { isFieldTouched, getFieldError }: WrappedFormUtils,
  fieldNames: string[],
  ignoreFieldTouchedCheck: boolean,
): {
  [fieldName: string]: ValidationProps
} {
  const isFieldError = (fieldName: string) =>
    (ignoreFieldTouchedCheck || isFieldTouched(fieldName)) &&
    getFieldError(fieldName)

  return fieldNames.reduce(
    (
      acc: {
        [fieldName: string]: ValidationProps
      },
      fieldName,
    ) => {
      const error = isFieldError(fieldName)
      return {
        ...acc,
        [fieldName]: {
          validateStatus: error ? "error" : "",
          help: error || "",
        },
      }
    },
    {},
  )
}

/**
 * Use this to convert formValues.weight values to an actual Weight object.
 * It does conversions to turn all the fields into valid Weight fields.
 */
export function formValuesToWeight(
  formValues?: Record<keyof Weight, any>,
): Weight | null {
  const { unit, value, count } = formValues ?? {}
  if (
    !stringValidations.moreThanZero.test(String(value)) ||
    (count && !stringValidations.moreThanZero.test(String(count))) ||
    !unit
  ) {
    return null
  }
  return {
    unit: String(unit),
    count:
      Number(value) > 0 && (Number(count) === 0 || count === undefined)
        ? 1
        : Number(count),
    value: Number(value),
  }
}

export function getAdvancedPricingRules(
  advancedPricingValidationConfig: AdvancedPricingConfig | null | undefined,
  initialValue: number | string | null | undefined,
  errMsgTextMap: {
    errorPriceDifference: string
    errorPriceExceed: string
  },
): ValidationRule[] {
  // const maxPriceDifference =
  //   advancedPricingValidationConfig?.maxPriceDifference ??
  //   0
  return advancedPricingValidationConfig !== null &&
    advancedPricingValidationConfig !== undefined // edge case, if not loaded don't load the validator - rely on BE to do validation
    ? [
        // {
        //   validator: newPriceDiffValidator(
        //     maxPriceDifference,
        //     errMsgTextMap.errorPriceDifference.replace(
        //       /<limit>/i,
        //       String(maxPriceDifference * 100),
        //     ),
        //     initialValue,
        //   ),
        // },
        {
          max: advancedPricingValidationConfig.maxPrice ?? Infinity,
          type: "number",
          message: errMsgTextMap.errorPriceExceed,
          transform: (val: any) =>
            val !== null && !Number.isNaN(Number(val)) ? Number(val) : 0,
        },
      ]
    : []
}
